import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type ViewMode = 'traditional' | 'ai';

interface IViewModeContextValue {
  mode: ViewMode;
  setMode: (mode: ViewMode) => void;
  toggleMode: () => void;
}

const ViewModeContext = createContext<IViewModeContextValue | undefined>(undefined);

interface IViewModeProviderProps {
  children: React.ReactNode;
  initialMode?: ViewMode;
}

export const ViewModeProvider: React.FC<IViewModeProviderProps> = ({
  children,
  initialMode = 'traditional',
}) => {
  const [mode, setMode] = useState<ViewMode>(initialMode);

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === 'traditional' ? 'ai' : 'traditional'));
  }, []);

  const value = useMemo(() => ({ mode, setMode, toggleMode }), [mode, toggleMode]);

  return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>;
};

export const useViewMode = (): IViewModeContextValue => {
  const ctx = useContext(ViewModeContext);
  if (!ctx) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return ctx;
};

export default ViewModeContext;
